import XIcon from "@mui/icons-material/Close"
import {
  Button,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { Event, EventImage, ImageOrientationEnum } from "@/common/types"
import dayjs from "dayjs"
import shareIcon from "@/components/mapBox/images/copy_icon.png"
import Flickity, { FlickityOptions } from "react-flickity-component"
import "flickity/css/flickity.css"
import { useState } from "react"
import ReactGA from "react-ga4"
import { useSnackbar } from "@/contexts/SnackbarContext"
import useFavoriteEvent from "@/hooks/api/events/useFavoriteEvent"
import useDialogState from "@/hooks/useDialogState"
import { useGetMe } from "@/hooks/useGetMe"
import LoginDialog from "./LoginDialog"
import { HeartFilledIcon } from "@/common/images/HeartFilledIcon"
import { HeartIcon } from "@/common/images/HeartIcon"

interface MobileLargeEventDialogProps {
  eventData: Event
  handleClose: () => void
  calendarDate?: string
}

const likeAndShareButtonStyling = {
  //   backgroundColor: "white",
  color: "white",
  width: "42px",
  height: "42px",
  //   padding: ".5rem",
  paddingX: "4px",
  paddingY: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: ".2rem",
  marginRight: ".2rem",
}

function Carousel({ images }: { images: EventImage[] }) {
  const flickityOptions: FlickityOptions = {
    initialIndex: 0,
    cellAlign: "left",
    groupCells: false,
    prevNextButtons: false,
  }

  const sortedImages = images
    .sort((image1, image2) => {
      if (image1.orientation !== image2.orientation) {
        return image1.orientation === ImageOrientationEnum.VERTICAL ? -1 : 1
      }
      return image1.image_order - image2.image_order
    })
    .filter((image) => image.orientation === ImageOrientationEnum.VERTICAL)

  return (
    <Flickity
      className={"carousel"}
      elementType={"div"}
      options={flickityOptions}
      disableImagesLoaded={false}
      reloadOnUpdate
      static
    >
      {sortedImages.map((image, idx) => {
        if (idx === 0) {
          return (
            <img
              key={image.event_image_id}
              src={image.image_url}
              alt="Event image"
              width="250px"
              height="auto"
              style={{
                // paddingLeft: "20px",
                borderRadius: "12px",
                marginRight: "40px",
              }}
            />
          )
        } else {
          return (
            <img
              key={image.event_image_id}
              src={image.image_url}
              alt="Event image"
              width="250px"
              height="auto"
              style={{ marginRight: "40px", borderRadius: "12px" }}
            />
          )
        }
      })}
    </Flickity>
  )
}

export default function MobileLargeEventDialog({
  eventData,
  handleClose,
  calendarDate,
}: MobileLargeEventDialogProps) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const { showSnackbar } = useSnackbar()
  const { data: me } = useGetMe()

  const {
    openDialog: openLoginDialog,
    closeDialog: closeLoginDialog,
    isOpen: loginIsOpen,
  } = useDialogState({})
  const { mutateAsync: favoriteEvent } = useFavoriteEvent()

  const handleActionClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    ReactGA.event({
      category: "Event",
      action: "Click action url",
      label: eventData.title,
      value: eventData.event_id,
    })

    setTimeout(() => {
      window.open(eventData.action_url, "_blank", "noopener,noreferrer")
    }, 300)
  }

  const copyToClipBoard = async (eventId: number) => {
    try {
      console.log(calendarDate)
      const dateParam = window.location.pathname.includes("calendar")
        ? `&date=${calendarDate}`
        : ""
      const shareUrl = `${window.location.origin}${location.pathname}?eventId=${eventId}&details=true${dateParam}`
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar("Copied link to event!", "success")
    } catch (err) {
      console.error("could not copy", err)
    }
  }

  const handleFavoriteEvent = () => {
    favoriteEvent(Number(eventData.event_id)).then((res) => {
      if (res.message === "Event favorited") {
        showSnackbar("Event Favorited", "success")
      } else {
        showSnackbar("Event removed from favorites", "error")
      }
    })
  }

  return (
    <>
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: "-16px", // Adjust as needed
          top: "-16px",
          backgroundColor: "black",
          color: "white",
          zIndex: 9999,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
          padding: "4px",
        }}
      >
        <XIcon />
      </IconButton>
      <DialogContent
        sx={{
          backgroundColor: "#2C2C2C",
          color: "white",
          padding: 2,
        }}
      >
        <Stack pb={2}>
          <Typography variant="h2" fontSize={20}>
            {eventData.title}
          </Typography>
          <Typography fontSize={14}>
            {eventData.location_title}, {eventData.address.split(", ")[1]}
          </Typography>
        </Stack>
        <Stack gap={2} sx={{ color: "white", paddingBottom: 2 }}>
          <Typography fontWeight={300}>
            {" "}
            {descriptionExpanded
              ? eventData.description
              : `${eventData.description.slice(0, 53)}...`}
            {descriptionExpanded ? (
              <span
                style={{
                  display: "block", // Ensures it's on a new line
                  color: "#7CFFD0",
                  cursor: "pointer",
                  textDecoration: "underline",
                  marginTop: "5px", // Adds space for better readability
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setDescriptionExpanded(false)
                }}
              >
                click to read less
              </span>
            ) : (
              <span
                style={{
                  color: "#7CFFD0",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: 300,
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setDescriptionExpanded(true)
                }}
              >
                click to view more
              </span>
            )}
          </Typography>
          <Stack direction="row">
            <Typography>
              {dayjs(eventData.event_start_timestamp).format("MMM D, h:mma")} -{" "}
              <span style={{ fontStyle: "italic" }}>{eventData.price}</span>
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} paddingTop={2}>
            <Stack
              direction={"row"}
              gap={1}
              width="100%"
              sx={{
                alignContent: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#147BE1",
                  borderRadius: 8,
                  paddingX: 2,
                  paddingY: 0,
                  height: "80%",
                  whiteSpace: "nowrap",
                }}
                onClick={(e) => handleActionClick(e)}
              >
                {eventData.button_label}
              </Button>
              <Button
                variant="contained"
                // startIcon={<LockOutlinedIcon sx={{ color: "#318200" }} />}
                onClick={() => {
                  // openDialog({})
                  showSnackbar("Collect Cards Feature Coming Soon!", "info")
                }}
                sx={{
                  backgroundColor: "#EAFCDE",
                  borderRadius: 8,
                  paddingX: 2,
                  color: "#318200",
                  paddingY: 0,
                  height: "80%",
                  "&.MuiButtonBase-root:hover": {
                    bgcolor: "transparent",
                  },
                  whiteSpace: "nowrap",
                }}
              >
                Collect Card
              </Button>
            </Stack>

            <Stack
              direction="row"
              gap={0.5}
              sx={{
                right: 0,
                alignItems: "center",
                justifyContent: "center",
                paddingY: "0",

                marginY: "0",
                zIndex: "+5",
              }}
            >
              <IconButton
                style={likeAndShareButtonStyling}
                onClick={(e) => {
                  e.stopPropagation()
                  copyToClipBoard(eventData.event_id)
                }}
              >
                <img
                  src={shareIcon}
                  alt="Share event"
                  style={{
                    width: "20px",
                    height: "20px",
                    filter:
                      "invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)",
                  }}
                />
              </IconButton>
              <IconButton
                style={likeAndShareButtonStyling}
                onClick={() => {
                  if (me.length) {
                    handleFavoriteEvent()
                  } else {
                    openLoginDialog({})
                  }
                }}
              >
                {eventData.favorited ? <HeartFilledIcon /> : <HeartIcon />}
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
        {eventData.event_images && <Carousel images={eventData.event_images} />}
        <LoginDialog
          closeDialog={closeLoginDialog}
          isOpen={loginIsOpen}
          message="Log in to favorite events!"
        />
      </DialogContent>
    </>
  )
}
